/* eslint-disable react-hooks/exhaustive-deps */
import { Button, ConfigProvider, Form, Input, message } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import http from '../../services/http';
import styles from './index.module.scss';
import ReCAPTCHA from 'react-google-recaptcha';
import Utils from '../../utils/Utils';
import AppConsts from '../../libs/AppConsts';
import { useTabs } from '../../hooks/UseTabs';
import { ArrowLeft, CheckCircleFill, Envelope, Lock, Person } from 'react-bootstrap-icons';

type State = 'Login' | 'Register' | 'Forgot' | 'Reset';

const CONTENT: Record<State, { title: string; subtitle: string }> = {
  Login: { title: 'С возвращением', subtitle: 'Войдите в аккаунт, чтобы продолжить' },
  Register: { title: 'Создайте аккаунт', subtitle: 'Это займёт меньше минуты' },
  Forgot: {
    title: 'Восстановление пароля',
    subtitle: 'Введите email, который используете для входа в аккаунт',
  },
  Reset: { title: 'Новый пароль', subtitle: 'Придумайте новый пароль для входа в аккаунт' },
};

export default function AuthView() {
  const [form] = Form.useForm();

  const { capchaToken, recaptchaRef, handleRecaptcha } = useRecaptcha();

  const [state, setState] = useState<State>('Login');

  const tabs = useTabs();

  const resetToken = tabs.get('token');

  const resetState = tabs.get('state');

  const [isLoading, setLoading] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const sourceParam = urlParams.get('source');

  useEffect(() => {
    if (sourceParam) {
      localStorage.setItem('360_source', sourceParam);
    }
    if (resetState === 'reset') {
      setState('Reset');
    }
  }, [resetState, resetToken]);

  const oAuth = (type: 'google' | 'vk') => {
    if (type === 'google') {
      window.location.href =
        AppConsts.host + `/auth/google`;
    }
    if (type === 'vk') {
      window.location.href = AppConsts.host + '/auth/vk';
    }
  };

  const onRegister = async () => {
    const values = form.getFieldsValue();

    setLoading(true);

    try {
      await form.validateFields();

      let dto = { ...values, token: capchaToken };

      if (sourceParam) {
        dto = { ...dto, source: sourceParam };
      }
      const result = await http.post('/auth/register', dto);

      if (result.data.access_token) {
        localStorage.setItem('token360', result.data.access_token);
        window.location.href = '/faq';
        await Utils.delay(100);
        window.location.reload();
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const onLogin = async () => {
    const values = form.getFieldsValue();

    setLoading(true);

    try {
      await form.validateFields();
      const result = await http.post('/auth/login', values);

      if (result.data.access_token) {
        localStorage.setItem('token360', result.data.access_token);
        window.location.href = '/faq';
        await Utils.delay(100);
        window.location.reload();
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const onSendForgot = async () => {
    setLoading(true);

    try {
      await form.validateFields();

      const values = form.getFieldsValue();

      await http.post('/auth/forgot', values);

      message.success({ content: 'Проверьте ваш электронный ящик' });

      setState('Login');
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const onSetNewPassword = async () => {
    setLoading(true);

    try {
      await form.validateFields();

      const values = form.getFieldsValue();

      const result = await http.post('/auth/reset', { ...values, token: resetToken });

      tabs.remove('state');
      tabs.remove('state');

      if (result.data.access_token) {
        localStorage.setItem('token360', result.data.access_token);
        window.location.href = '/faq';
        await Utils.delay(100);
        window.location.reload();
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const onSetRegister = () => {
    form.resetFields();
    setState('Register');
  };

  const onSetForgot = () => {
    form.resetFields();
    setState('Forgot');
  };

  const onSubmit = () => {
    if (state === 'Login') return onLogin();
    if (state === 'Register') return onRegister();
    if (state === 'Forgot') return onSendForgot();
    return onSetNewPassword();
  };

  const submitLabel =
    state === 'Login' ? 'Войти' : state === 'Forgot' ? 'Отправить' : 'Подтвердить';

  return (
    <div className={styles.area}>
      <div className={styles.brandPanel}>
        <div className={styles.brandGlowOne} />
        <div className={styles.brandGlowTwo} />
        <div className={styles.brandGrid} />
        <div className={styles.brandContent}>
          <div className={styles.logo}>
            Stafflow <div className={styles.number}>360</div>
          </div>
          <h2 className={styles.brandHeading}>Оценка 360°, которая помогает командам расти</h2>
          <p className={styles.brandText}>
            Собирайте обратную связь от коллег, руководителей и подчинённых в одном месте — и
            превращайте её в понятные решения для развития команды.
          </p>
          <ul className={styles.brandFeatures}>
            <li>
              <CheckCircleFill /> Прозрачная оценка компетенций
            </li>
            <li>
              <CheckCircleFill /> Готовые отчёты и аналитика
            </li>
            <li>
              <CheckCircleFill /> Удобно для HR и руководителей
            </li>
          </ul>
        </div>
      </div>

      <div className={styles.formPanel}>
        <ConfigProvider
          theme={{
            components: {
              Input: {
                lineHeight: 2.9,
              },
              Form: {
                itemMarginBottom: 20,
              },
            },
          }}
        >
          <div className={styles.formWrap}>
            <div className={styles.mobileLogo}>
              Stafflow <div className={styles.number}>360</div>
            </div>

            <div className={styles.formHeader}>
              <h1>{CONTENT[state].title}</h1>
              <p>{CONTENT[state].subtitle}</p>
            </div>

            <Form form={form} layout='vertical' disabled={isLoading} className={styles.form}>
              {state === 'Register' && (
                <div className={styles.nameRow}>
                  <Form.Item name='firstName' rules={[{ required: true, message: ' ' }]}>
                    <Input prefix={<Person />} placeholder='Имя' />
                  </Form.Item>
                  <Form.Item name='lastName' rules={[{ required: true, message: ' ' }]}>
                    <Input prefix={<Person />} placeholder='Фамилия' />
                  </Form.Item>
                </div>
              )}

              {state === 'Reset' && (
                <>
                  <Form.Item
                    label='Новый пароль'
                    name='password'
                    rules={[{ required: true, message: ' ' }]}
                  >
                    <Input.Password prefix={<Lock />} placeholder='••••••••' />
                  </Form.Item>
                  <Form.Item
                    label='Повторите новый пароль'
                    name='password2'
                    rules={[{ required: true, message: ' ' }]}
                  >
                    <Input.Password prefix={<Lock />} placeholder='••••••••' />
                  </Form.Item>
                </>
              )}

              {(state === 'Login' || state === 'Register' || state === 'Forgot') && (
                <Form.Item name='email' rules={[{ required: true, message: ' ' }]}>
                  <Input prefix={<Envelope />} placeholder='info@stafflow.by' />
                </Form.Item>
              )}
              {(state === 'Login' || state === 'Register') && (
                <Form.Item name='password' rules={[{ required: true, message: ' ' }]}>
                  <Input.Password prefix={<Lock />} placeholder='Пароль' />
                </Form.Item>
              )}

              {state === 'Register' && (
                <div className={styles.captcha}>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey='6Le0tFwqAAAAAKI8ILSRPN4qPhgjIlGZiAg-6DwZ'
                    onChange={handleRecaptcha}
                  />
                </div>
              )}

              <div className={styles.actions}>
                <Button block onClick={onSubmit} size='large' type='primary'>
                  {submitLabel}
                </Button>
              </div>
            </Form>

            {state === 'Login' && (
              <>
                <div className={styles.divider}>
                  <span>или</span>
                </div>
                <div className={styles.socialItem} onClick={() => oAuth('google')}>
                  <div className={styles.google} />
                  <p>Продолжить с Google</p>
                </div>
              </>
            )}

            <div className={styles.switchRow}>
              {state === 'Login' && (
                <>
                  <span onClick={onSetForgot} className={styles.link}>
                    Забыли пароль?
                  </span>
                  <span className={styles.dot}>·</span>
                  <span onClick={onSetRegister} className={styles.link}>
                    Регистрация
                  </span>
                </>
              )}
              {(state === 'Register' || state === 'Forgot' || state === 'Reset') && (
                <span onClick={() => setState('Login')} className={styles.link}>
                  <ArrowLeft /> Назад ко входу
                </span>
              )}
            </div>
          </div>
        </ConfigProvider>
      </div>
    </div>
  );
}

const useRecaptcha = () => {
  const [capchaToken, setCapchaToken] = useState<string>('');
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);

  const handleRecaptcha = useCallback((token: string | null) => {
    setCapchaToken(token || '');
  }, []);

  useEffect(() => {
    const refreshCaptcha = () => {
      if (recaptchaRef.current && capchaToken) {
        recaptchaRef.current.reset();
        setCapchaToken('');
      }
    };

    let tokenRefreshTimeout: NodeJS.Timeout | null = null;

    if (capchaToken) {
      tokenRefreshTimeout = setTimeout(refreshCaptcha, 110000); // 110 seconds
    }

    return () => {
      if (tokenRefreshTimeout) {
        clearTimeout(tokenRefreshTimeout);
      }
    };
  }, [capchaToken]);

  return { capchaToken, setCapchaToken, recaptchaRef, handleRecaptcha };
};
